import { useComposedRefs } from "@radix-ui/react-compose-refs";
import {
  Cross1Icon,
  ExitFullScreenIcon,
  VideoIcon,
  Cross2Icon,
  EnterFullScreenIcon,
} from "@radix-ui/react-icons";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Editor } from "tldraw";

import { ErrorBanner } from "@/components/Callout";
import {
  CanvasEditor,
  CanvasEditorHandle,
  CanvasEditorProps,
  DefaultCustomNodeIdContext,
} from "@/components/canvas-editor/CanvasEditor";
import { LazyRenderBlocksContext } from "@/components/canvas-editor/components/Container";
import { centerContent } from "@/components/canvas-editor/utils";
import { EditorSelector } from "@/components/EditorSelector";
import { FeedAmendmentProposal } from "@/components/FeedAmendmentProposal";
import { useIsMobile } from "@/components/hooks/use-mobile";
import { useEvent } from "@/components/hooks/useEvent";
import { useFullscreen } from "@/components/hooks/useFullscreen";
import { Spinner } from "@/components/Spinner";
import { Button, ButtonLink } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Toggle } from "@/components/ui/toggle";
import {
  amendAlgorithmTemplate,
  FeedAmendment,
  getAlgorithmTemplate,
} from "@/lib/api";
import { fillIfEmpty } from "@/lib/set-form-field";
import { FeedVersion, FilterTypes } from "@/lib/types";
import { cn, isVideoFeed } from "@/lib/utils";

const messages = [
  "Herding all your feeds into one place...",
  "Convincing RSS feeds to play nicely together...",
  "Teaching APIs to share their toys...",
  "Untangling your social media spaghetti...",
  "Building bridges between your favorite content...",
  "Organizing chaos one feed at a time...",
  "Negotiating peace treaties between conflicting feeds...",
  "Politely asking Twitter to share its secrets...",
  "Connecting dots and crossing t's in your feeds...",
  "Making your content soup extra delicious...",
];

// An amendment is a different job from a generate, and takes a few seconds
// rather than half a minute, so it says what it is actually doing instead of
// borrowing the generator's jokes.
const amendMessages = [
  "Reading your feed...",
  "Working out the smallest change...",
  "Checking the custom node marketplace...",
  "Making sure it still draws...",
];

function RotatingFunnyMessages({
  from = messages,
}: {
  from?: string[];
} = {}) {
  const [message, setMessage] = useState(from[0]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessage(from[Math.floor(Math.random() * from.length)]);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, [from]);

  return message;
}

function readFormField(form: HTMLFormElement | null | undefined, name: string) {
  const field = form?.elements.namedItem(name);
  return field instanceof HTMLInputElement ||
    field instanceof HTMLTextAreaElement
    ? field.value || undefined
    : undefined;
}

export interface FeedEditorHandle {
  setFullscreen: (fullscreen: boolean) => void;
  setTemplateMode: (templateMode: boolean) => void;
}

function flattenNodes(filter: FilterTypes | undefined): FilterTypes[] {
  if (!filter) return [];

  if ("and" in filter) {
    return [filter, ...filter.and.flatMap(flattenNodes)];
  }

  if ("or" in filter) {
    return [filter, ...filter.or.flatMap(flattenNodes)];
  }

  return [filter];
}

export function FeedEditor({
  feedVersion,
  feedId,
  onChange,
  fullscreenActions,
  defaultFullscreen,
  handle,
  centerContent: centerContentProp,
  templateMode: templateModeProp,
  onTemplateModeChange: onTemplateModeChangeProp,
  rootNode,
  editorRef: editorRefProp,
  defaultCustomNodeId,
  onEdit,
  testPosts,
  onDebugPost,
  defaultMode = "canvas",
}: {
  feedVersion?: FeedVersion;
  feedId?: number;
  onChange?: (output: FeedVersion) => void;
  fullscreenActions?: React.ReactNode;
  defaultFullscreen?: boolean;
  handle?: React.RefObject<FeedEditorHandle>;
  editorRef?: React.RefObject<Editor | undefined>;
  centerContent?: boolean;
  templateMode?: boolean;
  onTemplateModeChange?: (templateMode: boolean) => void;
  rootNode?: CanvasEditorProps["rootNode"];
  defaultCustomNodeId?: number;
  onEdit?: () => void;
  testPosts?: string[];
  onDebugPost?: (urls: string[]) => void;
  defaultMode?: "canvas" | "json";
}) {
  const [isComplexFeed, setIsComplexFeed] = useState(false);
  const onMount = useEvent(() => {
    setIsComplexFeed(flattenNodes(feedVersion?.manifest.filter).length > 40);
  });
  const isMobile = useIsMobile();
  const [editor, setEditor] = useState<"canvas" | "json">(defaultMode);
  const editorRefInternal = useRef<Editor>(undefined);
  const editorRef = useComposedRefs(editorRefInternal, editorRefProp);
  const canvasHandle = useRef<CanvasEditorHandle>(null!);
  const [fullscreen, setFullscreen] = useFullscreen({
    editorRef: editorRefInternal,
    defaultFullscreen,
  });
  useEffect(() => {
    if (fullscreen) {
      document.body.classList.add("fullscreen-editor");
    } else {
      document.body.classList.remove("fullscreen-editor");
    }

    return () => {
      document.body.classList.remove("fullscreen-editor");
    };
  }, [fullscreen]);
  const [templateMode, setTemplateMode] = useState(templateModeProp);
  const onTemplateModeChange = useCallback(
    (templateMode: boolean) => {
      setTemplateMode(templateMode);
      onTemplateModeChangeProp?.(templateMode);
    },
    [onTemplateModeChangeProp],
  );
  const onGenerateTemplate = useMutation({
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async ({
      value,
      form,
    }: {
      value: string;
      form?: HTMLFormElement | null;
    }) => {
      const { manifest, name, slug, description } = await getAlgorithmTemplate({
        term: value,
      });

      canvasHandle.current.setManifest({
        ...(feedVersion as FeedVersion),
        manifest,
      });

      // The generator names and describes the feed too. Fill those in only where the
      // user has not already written something, so this stays additive when generating
      // from an existing feed. Limits mirror each input's own validation.
      fillIfEmpty(form, "display_name", name, 24);
      fillIfEmpty(form, "record_name", slug, 15);
      fillIfEmpty(form, "description", description, 300);

      onTemplateModeChange(false);
      setEditor("canvas");
    },
  });

  // An amendment is applied to the canvas as soon as it arrives -- reviewing a
  // change to a feed means looking at the feed, and a summary next to an
  // unchanged canvas asks the author to imagine the result. `beforeAmendment`
  // is what Discard puts back, captured from the CANVAS rather than from
  // `feedVersion` so that unsaved edits survive a discard.
  const [amendMode, setAmendMode] = useState(false);
  const [proposal, setProposal] = useState<FeedAmendment | null>(null);
  const beforeAmendment = useRef<FeedVersion | undefined>(undefined);

  const onAmendDesign = useMutation({
    onError: (error: AxiosError) => console.error(error),
    mutationFn: async ({
      value,
      form,
    }: {
      value: string;
      form?: HTMLFormElement | null;
    }) => {
      const before = canvasHandle.current.getFeedState();
      if (!before) {
        throw new Error("The feed builder is still loading.");
      }

      const previous: FeedVersion = {
        ...(feedVersion as FeedVersion),
        ...before,
      };

      const amendment = await amendAlgorithmTemplate({
        manifest: before.manifest,
        instruction: value,
        // "make it less US-centric" reads differently against a cycling feed
        // than against an elections one, and the form holds the only
        // description of what this feed is for.
        feedContext: {
          name: readFormField(form, "display_name"),
          description: readFormField(form, "description"),
        },
      });

      beforeAmendment.current = previous;
      canvasHandle.current.setManifest({
        ...previous,
        manifest: amendment.manifest,
      });
      setProposal(amendment);
      setAmendMode(false);
      setEditor("canvas");
    },
  });

  const submitAmendment = useCallback(
    (promptEl: HTMLTextAreaElement | null) => {
      const value = promptEl?.value?.trim();
      if (!value) return;
      onAmendDesign.mutate({ value, form: promptEl?.closest("form") });
    },
    [onAmendDesign],
  );

  const onAcceptAmendment = useCallback(() => {
    // The canvas already holds it, and its own change event has already told
    // the form. Accepting is just letting go of the undo.
    beforeAmendment.current = undefined;
    setProposal(null);
  }, []);

  const onDiscardAmendment = useCallback(() => {
    if (beforeAmendment.current) {
      canvasHandle.current.setManifest(beforeAmendment.current);
    }
    beforeAmendment.current = undefined;
    setProposal(null);
  }, []);

  const hasActiveFeed = Boolean(feedVersion?.created_at);
  const defaultVisiblePanel =
    isMobile || !hasActiveFeed ? undefined : onChange ? "post" : "feed";

  const onCenterContent = useEvent(() => {
    if (!editorRefInternal.current) return;
    centerContent(editorRefInternal.current, {
      accountForSidebar: Boolean(!isMobile && defaultVisiblePanel),
    });
  });

  useEffect(() => {
    if (!centerContentProp) return;

    const interval = setInterval(() => {
      if (editorRefInternal.current) {
        onCenterContent();
        clearInterval(interval);
      }
    }, 10);

    return () => {
      clearInterval(interval);
    };
  }, [centerContentProp, onCenterContent, isMobile]);

  useImperativeHandle(
    handle,
    () => ({
      setFullscreen,
      setTemplateMode: onTemplateModeChange,
    }),
    [setFullscreen, onTemplateModeChange],
  );

  const editorSelect = <EditorSelector editor={editor} setEditor={setEditor} />;
  const isVideo = isVideoFeed(feedVersion);
  const isCustomNode = rootNode?.variant === "custom-node";

  const topBarActions = (
    <>
      <Toggle
        size="xs"
        aria-label={fullscreen ? "Exit Full Screen" : "Enter Full Screen"}
        pressed={fullscreen}
        onPressedChange={setFullscreen}
      >
        <span className="sr-only">
          {fullscreen ? "Exit Full Screen" : "Enter Full Screen"}
        </span>
        {fullscreen ? (
          <ExitFullScreenIcon className="h-3 w-3" />
        ) : (
          <EnterFullScreenIcon className="h-3 w-3" />
        )}
      </Toggle>
      {defaultFullscreen ? (
        <>
          <ButtonLink
            size="xs"
            variant="outline"
            to={
              isCustomNode
                ? "/app/custom-nodes/$nodeId/view"
                : "/app/feed/$feedId/view"
            }
            className="hidden md:flex"
          >
            Cancel
          </ButtonLink>
          <ButtonLink
            size="smIcon"
            variant="outline"
            to={
              isCustomNode
                ? "/app/custom-nodes/$nodeId/view"
                : "/app/feed/$feedId/view"
            }
            className="md:hidden"
          >
            <span className="sr-only">Cancel</span>
            <Cross2Icon />
          </ButtonLink>
        </>
      ) : null}
      {fullscreen && fullscreenActions}
    </>
  );

  useEffect(() => {
    if (!fullscreen) return;

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [fullscreen]);

  return (
    <LazyRenderBlocksContext value={isComplexFeed}>
      <DefaultCustomNodeIdContext value={defaultCustomNodeId}>
        <div
          className={cn(
            "w-full flex overflow-hidden relative feed-editor",
            fullscreen
              ? "fixed inset-0 z-10 bg-slate-app"
              : "h-[580px] rounded-lg border border-slate-6 dark:border-slatedark-6",
          )}
        >
          <div className="flex-1 flex relative">
            {isVideo && (
              <div
                className={cn(
                  "z-50 rounded px-3 py-1 bg-purplea-6 dark:bg-purpledarka-6 text-purple-12 dark:text-purpledark-12 border border-purple-8 dark:border-purpledark-8 absolute flex items-center gap-2 shadow-md",
                  "left-1/2 -translate-x-1/2",
                  isMobile ? "top-14" : "top-2",
                )}
              >
                <VideoIcon />
                <span>Video Feed</span>
              </div>
            )}
            <CanvasEditor
              rootNode={rootNode}
              ref={editorRef as unknown as React.RefObject<Editor>}
              feedId={feedId}
              imperativeHandle={canvasHandle}
              feedVersion={feedVersion}
              fullscreen={fullscreen}
              setFullscreen={setFullscreen}
              onUpdate={onChange}
              defaultVisiblePanel={
                isMobile || !hasActiveFeed
                  ? undefined
                  : onChange
                  ? undefined
                  : "feed"
              }
              onViewJson={() => setEditor("json")}
              editorActions={editorSelect}
              topBarActions={topBarActions}
              onStartTemplateMode={() => onTemplateModeChange(true)}
              onStartAmendMode={() => {
                // Starting a new amendment over an unreviewed one would leave
                // the author with no way back to where they started.
                onDiscardAmendment();
                setAmendMode(true);
              }}
              onEdit={onEdit}
              testPosts={testPosts}
              onDebugPost={onDebugPost}
              onMount={onMount}
              mode={editor}
            />
          </div>

          {proposal && !amendMode && (
            <FeedAmendmentProposal
              amendment={proposal}
              onAccept={onAcceptAmendment}
              onDiscard={onDiscardAmendment}
            />
          )}

          {amendMode &&
            (onAmendDesign.isPending ? (
              <div className="absolute inset-0 flex items-center justify-center z-100 bg-slate-subtle">
                <div className="flex flex-col gap-4 w-full px-8 max-w-2xl mx-auto">
                  <Spinner />
                  <div className="text-center text-sm text-slate-dim">
                    <RotatingFunnyMessages from={amendMessages} />
                  </div>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center z-100 bg-slate-subtle">
                <div className="w-full">
                  <div className="flex flex-col gap-4 w-full px-8 max-w-2xl mx-auto">
                    <div>
                      <Label htmlFor="amend-mode-description">
                        Change this feed
                      </Label>
                      <p className="text-sm text-slate-dim mt-1">
                        Describe one change. Everything else stays as it is, and
                        you can discard the result.
                      </p>
                    </div>
                    <Textarea
                      id="amend-mode-description"
                      placeholder="Now make it omit anything political."
                      onKeyDown={(e) => {
                        // A one-line instruction should not need a mouse.
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          // preventDefault matters: this textarea lives inside
                          // the feed's own form, and letting the key through
                          // would submit the FEED rather than the instruction.
                          e.preventDefault();
                          submitAmendment(e.currentTarget);
                        }
                      }}
                    />
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        submitAmendment(
                          document.getElementById(
                            "amend-mode-description",
                          ) as HTMLTextAreaElement | null,
                        );
                      }}
                    >
                      Propose change
                    </Button>
                  </div>
                </div>
                <Button
                  size="smIcon"
                  variant="ghost"
                  onPress={() => setAmendMode(false)}
                  className="absolute top-4 right-4"
                >
                  <Cross1Icon />
                </Button>
                {onAmendDesign.error && (
                  <ErrorBanner error={onAmendDesign.error} />
                )}
              </div>
            ))}

          {templateMode &&
            (onGenerateTemplate.isPending ? (
              <div className="absolute inset-0 flex items-center justify-center z-100 bg-slate-subtle">
                <div className="flex flex-col gap-4 w-full px-8 max-w-2xl mx-auto">
                  <Spinner />
                  <div className="text-center text-sm text-slate-dim">
                    <RotatingFunnyMessages />
                  </div>
                </div>
              </div>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center z-100 bg-slate-subtle">
                <div className="w-full">
                  <div className="flex flex-col gap-4 w-full px-8 max-w-2xl mx-auto">
                    <Label htmlFor="template-mode-description">
                      Describe your feed
                    </Label>
                    <Textarea
                      id="template-mode-description"
                      placeholder="A feed about pottery."
                    />
                    <Button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        const promptEl = document.getElementById(
                          "template-mode-description",
                        ) as HTMLTextAreaElement | null;
                        const value = promptEl?.value;

                        if (!value) return;
                        onGenerateTemplate.mutate({
                          value,
                          form: promptEl?.closest("form"),
                        });
                      }}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
                <Button
                  size="smIcon"
                  variant="ghost"
                  onPress={() => onTemplateModeChange(false)}
                  className="absolute top-4 right-4"
                >
                  <Cross1Icon />
                </Button>
                {onGenerateTemplate.error && (
                  <ErrorBanner error={onGenerateTemplate.error} />
                )}
              </div>
            ))}
        </div>
      </DefaultCustomNodeIdContext>
    </LazyRenderBlocksContext>
  );
}
